// Client-side hero ability catalog — mirrors Rust content/hero_abilities.rs.
// Used to look up ability names, descriptions, and roles from ability IDs
// sent in SceneNode.props.hero.abilitySlots.

export interface HeroAbilityInfo {
  id: string;
  name: string;
  cooldownTicks: number;
  minLevel: number;
  roles: string[];
  description: string;
}

const ABILITY_CATALOG: Record<string, HeroAbilityInfo> = {
  rally: {
    id: 'rally',
    name: 'Rally',
    cooldownTicks: 60,
    minLevel: 1,
    roles: ['General'],
    description: 'Restore 30 morale to the hero.',
  },
  steel_wall: {
    id: 'steel_wall',
    name: 'Steel Wall',
    cooldownTicks: 45,
    minLevel: 1,
    roles: ['General'],
    description: '+30% defense, +2 morale/tick to nearby allies for 30 ticks.',
  },
  charge: {
    id: 'charge',
    name: 'Charge',
    cooldownTicks: 30,
    minLevel: 3,
    roles: ['General'],
    description: '+50% attack to nearby allies for 10 ticks.',
  },
  coordinated_strike: {
    id: 'coordinated_strike',
    name: 'Coordinated Strike',
    cooldownTicks: 30,
    minLevel: 1,
    roles: ['General'],
    description: '+20% attack to nearby allies for 15 ticks (radius 3).',
  },
  cleave: {
    id: 'cleave',
    name: 'Cleave',
    cooldownTicks: 12,
    minLevel: 1,
    roles: ['General'],
    description: '+80% attack to adjacent allies for 1 tick.',
  },
  defensive_fortify: {
    id: 'defensive_fortify',
    name: 'Defensive Fortify',
    cooldownTicks: 45,
    minLevel: 3,
    roles: ['General'],
    description: '+40% defense, +1 morale/tick to nearby allies for 20 ticks.',
  },
  insight: {
    id: 'insight',
    name: 'Insight',
    cooldownTicks: 90,
    minLevel: 1,
    roles: ['Sage'],
    description: 'Grant 1 doctrine point.',
  },
  sacred_heal: {
    id: 'sacred_heal',
    name: 'Sacred Heal',
    cooldownTicks: 12,
    minLevel: 1,
    roles: ['Sage', 'Champion'],
    description: 'Heal the hero for 100 HP.',
  },
  tempest: {
    id: 'tempest',
    name: 'Tempest',
    cooldownTicks: 90,
    minLevel: 5,
    roles: ['Sage'],
    description: 'AoE aura for 8 ticks (radius 3).',
  },
  plant_whispers: {
    id: 'plant_whispers',
    name: 'Plant Whispers',
    cooldownTicks: 60,
    minLevel: 1,
    roles: ['Spymaster'],
    description: 'Espionage operation bonus.',
  },
  counter_spy: {
    id: 'counter_spy',
    name: 'Counter-Spy',
    cooldownTicks: 45,
    minLevel: 3,
    roles: ['Spymaster'],
    description: 'Counter-espionage operation bonus.',
  },
  envoy: {
    id: 'envoy',
    name: 'Envoy',
    cooldownTicks: 45,
    minLevel: 1,
    roles: ['Diplomat'],
    description: 'Diplomatic action bonus.',
  },
  charm: {
    id: 'charm',
    name: 'Charm',
    cooldownTicks: 120,
    minLevel: 5,
    roles: ['Diplomat'],
    description: 'Powerful diplomatic action bonus.',
  },
  champion_aura: {
    id: 'champion_aura',
    name: 'Champion Aura',
    cooldownTicks: 60,
    minLevel: 1,
    roles: ['Champion'],
    description: '+15% attack, +15% defense, +1 morale/tick to nearby allies for 30 ticks.',
  },
  stewardship: {
    id: 'stewardship',
    name: 'Stewardship',
    cooldownTicks: 60,
    minLevel: 1,
    roles: ['Steward'],
    description: '+20% defense, +0.5 morale/tick to nearby allies for 30 ticks.',
  },
};

export function getAbilityInfo(abilityId: string): HeroAbilityInfo | undefined {
  return ABILITY_CATALOG[abilityId];
}

export function getAllAbilities(): HeroAbilityInfo[] {
  return Object.values(ABILITY_CATALOG);
}

export function getAbilitiesForRole(role: string, level: number): HeroAbilityInfo[] {
  return Object.values(ABILITY_CATALOG).filter(
    (a) => a.roles.includes(role) && a.minLevel <= level,
  );
}
